// Basic
import React, { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useCustomPayStore from '@/app/store/customPay';
import useSessionStore from '@/app/store/session';
// Components
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import CustomPayConnectionPointsPanel from './CustomPayConnectionPointsPanel';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import ModalAddPort from '../modals/addPort/ModalAddPort';
import { Separator } from '@/common-utils/components/ui/separator';
// Common
import organizationFields from '../../fields/organizationFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import ORGANIZATION_TYPES from '../../enums/OrganizationTypes';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import ICONS from '@/common-utils/enums/icons';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.organization
 * @param root0.filteredConnectionPoints
 * @param root0.setLaunchRefetch
 * @returns {JSX} ConnectionPointsPanel
 */
function ConnectionPointsPanel({ filteredConnectionPoints, organization, setLaunchRefetch }) {

    // Hooks
    const permissions = useSessionStore(s => s.session.permissions);
    const { isCustomPay } = useCustomPayStore();

    // State
    const [showModalAddPort, setShowModalAddPort] = useState(false);

    // Constants
    const connectionPointsDataToDisplay = {
        ipAddress: organizationFields.CONNECTION_POINTS.IP_ADDRESS,
        port: organizationFields.CONNECTION_POINTS.PORT
    };

    // Functions
    const isOfType = (...organizationTypes) => {
        return organization && organizationTypes.some(organizationType => organization[organizationFields.TYPE.key] === organizationType.name);
    };

    return (
        <div>
            <PageActions
                actions={
                    [
                        {
                            icon: ICONS.ADD,
                            entity: 'actions.page.add_port',
                            namespace: nsOrganization,
                            onClick: () => setShowModalAddPort(true),
                            visible: filteredConnectionPoints.length === 0 && !isCustomPay && isOfType(ORGANIZATION_TYPES.WHITE_LABEL),
                            disabled: !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.MB_ADMIN.name]
                            }),
                            tooltipForbidden: 'title.permissions.access_denied'
                        }
                    ]
                }
            />
            {
                isCustomPay ?
                    <CustomPayConnectionPointsPanel
                        organization={organization}
                        organizationConnectionPointsFields={connectionPointsDataToDisplay}
                        filteredConnectionPoints={filteredConnectionPoints}
                    />
                    : filteredConnectionPoints.map((connectionPoint, index) => {
                        const connectionPointsData = {
                            [generateNestedFieldAccessor(organizationFields.CONNECTION_POINTS.IP_ADDRESS)]: connectionPoint.ipAddress,
                            [generateNestedFieldAccessor(organizationFields.CONNECTION_POINTS.PORT)]: connectionPoint.port
                        };
                        return (
                            <React.Fragment key={connectionPoint.uuid}>
                                <DetailsGrid data={connectionPointsData} fields={connectionPointsDataToDisplay} />
                                {
                                    index < filteredConnectionPoints.length - 1 &&
                                    <Separator className='my-4' />
                                }
                            </React.Fragment>
                        );
                    })

            }
            {
                showModalAddPort && (
                    <ModalAddPort
                        isOpen={showModalAddPort}
                        setIsOpen={setShowModalAddPort}
                        organizationId={organization[organizationFields.UUID.key]}
                        setLaunchRefetch={setLaunchRefetch}
                    />
                )
            }
        </div>
    );
}

ConnectionPointsPanel.propTypes = {
    filteredConnectionPoints: PropTypes.array.isRequired,
    organization: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ConnectionPointsPanel;